import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { Bell, Mail, MapPin, ShieldCheck } from 'lucide-react';
import { SiVisa, SiStripe } from 'react-icons/si';

/**
 * Real brand marks, not text labels or generic icon-font glyphs. Visa and
 * Stripe come from react-icons/si (the actual simplified brand marks).
 * Mastercard isn't a single-color silhouette in reality — it's two
 * overlapping circles (red + orange) with a distinct orange overlap wedge —
 * so it's drawn here at native brand colors instead of a monochrome glyph,
 * which would just look like an outline rather than the recognizable logo.
 */
function MastercardMark() {
  return (
    <svg viewBox="0 0 38 24" className="h-6 w-9" role="img" aria-label="Mastercard">
      <circle cx="15" cy="12" r="11" fill="#EB001B" />
      <circle cx="23" cy="12" r="11" fill="#F79E1B" />
      <path d="M19 3.6a10.98 10.98 0 0 1 0 16.8 10.98 10.98 0 0 1 0-16.8z" fill="#FF5F00" />
    </svg>
  );
}

/**
 * DHMAD.tn has no widely distributed brand icon set (unlike Visa/Mastercard/
 * Stripe), so this is a clean custom wordmark badge instead of a fabricated
 * logo — the shield reflects what DHMAD actually is here: an escrow payment
 * option for Tunisia/North Africa.
 */
function DhmadMark() {
  return (
    <span className="inline-flex items-center gap-1.5 px-1">
      <ShieldCheck className="h-4 w-4 text-emerald-600" />
      <span className="text-sm font-bold tracking-tight text-gray-800">DHMAD</span>
    </span>
  );
}

const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer className="bg-[#0A0A0A] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-[#FF3B30] rounded-sm flex items-center justify-center">
                <Bell className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-xl">VisaAlerts</span>
            </div>
            <p className="text-gray-400 text-sm mb-4 max-w-md">
              Never miss a visa appointment again. Get instant SMS & email alerts when new slots open at TLS Contact, VFS Global, and BLS centers.
            </p>
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
              <MapPin className="w-4 h-4" />
              <span>{t('footer_company')} - {t('footer_location')}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <Mail className="w-4 h-4" />
              <a href="mailto:customer_service@visago-alerts.online" className="hover:text-white transition-colors">
                customer_service@visago-alerts.online
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-400 hover:text-white text-sm transition-colors" data-testid="footer-home">
                  {t('nav_home')}
                </Link>
              </li>
              <li>
                <a href="/#pricing" className="text-gray-400 hover:text-white text-sm transition-colors" data-testid="footer-pricing">
                  {t('nav_pricing')}
                </a>
              </li>
              <li>
                <Link to="/contact" className="text-gray-400 hover:text-white text-sm transition-colors" data-testid="footer-contact">
                  {t('nav_contact')}
                </Link>
              </li>
              <li>
                <Link to="/impressum" className="text-gray-400 hover:text-white text-sm transition-colors">
                  Impressum
                </Link>
              </li>
            </ul>
          </div>

          {/* Payment Methods */}
          <div>
            <h4 className="font-heading font-semibold mb-4">Payment Methods</h4>
            <div className="flex flex-wrap items-center gap-2">
              {/* Visa */}
              <div className="bg-white rounded-md px-3 h-10 flex items-center shadow-sm hover:shadow-md transition-shadow" data-testid="payment-visa">
                <SiVisa className="h-6 w-11 text-[#1434CB]" />
              </div>
              {/* Mastercard */}
              <div className="bg-white rounded-md px-3 h-10 flex items-center shadow-sm hover:shadow-md transition-shadow" data-testid="payment-mastercard">
                <MastercardMark />
              </div>
              {/* Stripe */}
              <div className="bg-white rounded-md px-3 h-10 flex items-center shadow-sm hover:shadow-md transition-shadow" data-testid="payment-stripe">
                <SiStripe className="h-6 w-11 text-[#635BFF]" />
              </div>
              {/* DHMAD.tn */}
              <div className="bg-white rounded-md px-3 h-10 flex items-center shadow-sm hover:shadow-md transition-shadow" data-testid="payment-dhmad">
                <DhmadMark />
              </div>
            </div>
            <p className="text-gray-500 text-xs mt-3">Card payments secured by Stripe · DHMAD escrow for Tunisia &amp; North Africa</p>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm">
            © {new Date().getFullYear()} {t('footer_company')}. {t('footer_rights')}.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/impressum" className="text-gray-400 hover:text-white text-sm transition-colors">
              Impressum / Legal
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
